<script setup>
    import DOMPurify from "dompurify";
    import get from "lodash/get";
    import moment from "moment";
    import { computed, defineProps, toRefs } from "vue";

    const props = defineProps({
        column: {
            type: Object,
            required: true,
        },
        item: {
            type: Object,
            required: true,
        },
        slim: {
            type: Boolean,
            default: false,
        },
    });

    const { column, item } = toRefs(props);

    const contentByComputed = () => column.value.getContent.compute(item.value);

    const contentByGetContent = () => {
        if (column.value?.type === "list") {
            return getItems(item.value, column.value);
        }

        const contentResult = getContent(item.value, column.value);

        if (column.value?.type === "time") {
            return getTime(contentResult);
        }

        if (column.value?.type === "boolean") {
            return contentResult ? "Ja" : "Nej";
        }

        if (column.value?.type === "date") {
            return getDate(contentResult);
        }

        if (column.value?.type === "datetime") {
            return getDateTime(contentResult);
        }

        if (column.value?.type === "adress") {
            return getAdress(contentResult);
        }

        if (column.value?.type === "mall") {
            return splitStringto2Rows(contentResult);
        }

        return contentResult;
    };

    const content = computed(
        // Använd computed-funktionen om den är satt, annars default getContent
        typeof column.value?.getContent?.compute === "function" ? contentByComputed : contentByGetContent
    );

    function getIcon(itm, col) {
        const value = get(itm, col.iconName ?? col.name, "");
        return get(col.icons, value, "");
    }

    function getContent(itm, col) {
        // if col has getContent function, use that
        if (typeof col.getContent === "function") {
            return col.getContent(itm);
        }

        const content = get(itm, col.name, col.dflt ?? "");

        if (col.maxChars && content.length > col.maxChars) {
            return content.substring(0, col.maxChars) + "...";
        }

        return content;
    }
    /**
     *  @param {string|string[]} value
     *  Hanterar tooltip-innehåll om enkild text eller en lista av flera texter
     *
     */
    function getTooltipContent(value) {
        if (!value) {
            return "";
        }

        const doubleLineBreak = "<br><br>"; // Dubbla radbrytningar för en tom rad mellan texterna
        const content = Array.isArray(value) ? value.join(doubleLineBreak) : value;

        const config = {
            ALLOWED_TAGS: ["br"], // Tillåt endast radbrytningar
        };

        return DOMPurify.sanitize(content, config);
    }

    function getItems(itm, col) {
        const list = get(itm, col.listName, []);
        return list?.map((i) => getContent(i, col)) ?? [];
    }

    function getTime(value) {
        return typeof value === "string" ? value.substring(0, 5) : "";
    }

    function getDate(value) {
        return typeof value === "string" ? value.substring(0, 10) : "";
    }

    function getDateTime(value) {
        return typeof value === "string" ? moment(value).format("YYYY-MM-DD HH:mm") : "";
    }

    function splitStringto2Rows(value) {
        if (value?.length > 20) {
            const lastSpaceIndex = value.lastIndexOf(" ", 20);
            if (lastSpaceIndex !== -1) {
                const firstPart = value.slice(0, lastSpaceIndex);
                const secondPart = value.slice(lastSpaceIndex + 1);
                return `${firstPart} \n ${secondPart}`;
            }
        }

        return value;
    }

    function getAdress(itm) {
        const adressRad1 = itm?.adressRad1 ?? "";
        const adressRad2 = itm?.adressRad2 ?? "";

        let adress = "";

        if (adressRad1.length > 0 && adressRad2.length > 0) {
            adress = adressRad1;
        } else if (adressRad1.length === 0 && adressRad2.length !== 0) {
            adress = adressRad2;
        } else if (adressRad1.length > 0 && adressRad2.length === 0) {
            adress = itm.adressRad1;
        }

        return adress;
    }
</script>

<template>
    <div
        :title="column.trimOverflow && `${getContent(item, column)}`"
        class="kliniken-table-cell"
        :style="{
            background: column.background,
        }"
        :class="{
            slim,
            'trim-overflow': column.trimOverflow,
            draggable: column.draggable && column.draggable(item),
            'fc-event': column.draggable && column.draggable(item),
            finished: column.finished && column.finished(item),
        }"
        :data-event="column.draggableData ? column.draggableData(item) : ''"
        :id="item.id"
        v-tooltip="{
            placement: 'top',
            content: getTooltipContent(column.getTooltipText?.(item)),
            theme: 'kliniken-tooltip',
            html: true,
        }"
    >
        <img v-if="column.finished && column.finished(item)" src="@/assets/check_circle_black.svg" />
        <component v-if="column.component" :is="column.component" :item="item" :column="column" />
        <template v-else>
            <img v-if="column.icons" :src="getIcon(item, column)" alt="" />

            <ul v-if="column.type === 'list'">
                <li v-for="(i, index) in getItems(item, column)" :key="index">
                    {{ i }}
                </li>
            </ul>
            <span class="showmulti-rows" v-else-if="column.type === 'mall'">
                {{ splitStringto2Rows(content) }}
            </span>
            <span v-else>
                {{ content }}
            </span>
        </template>
    </div>
</template>

<style scoped lang="scss">
    @import "@/style/_variables.scss";

    .kliniken-table-cell {
        margin: 0;
        font-size: 14px;
        line-height: 16px;
        color: $color-text-black;
        font-family: Roboto, sans-serif;
        display: flex;
        max-width: 100%;
        align-items: center;

        &:not(.slim) {
            width: max-content;
        }

        img {
            margin-right: 0.5rem;
        }

        ul {
            margin-block-start: 0;
            margin-block-end: 0;
            margin-inline-start: 0;
            padding-inline-start: 0;

            li {
                list-style: none;
            }
        }

        .showmulti-rows {
            white-space: pre-wrap;
        }

        &.draggable {
            // Behövs för att kunna göra det draggable i ett element som har ::before med position absolute
            position: relative;

            border-radius: 4px;
            border: 1px dashed #7cbed6;
            padding: 4px;

            span {
                // center text vertically
                display: flex;
                align-items: center;

                height: 30px;
                padding: 5px;
                color: $color-active-dark;
                background-color: rgba(38, 148, 188, 0.2);
                border-left: 6px solid rgba(38, 148, 188, 0.5);
            }
        }

        &.trim-overflow {
            > * {
                max-width: 100%;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: pre;
            }
        }
    }
</style>
